/**
 * CSV Parser utility for OpenSkiData files
 */

/**
 * Parse CSV text into an array of objects
 */
export function parseCSV(csvText) {
  const lines = csvText.trim().split('\n');
  if (lines.length < 2) return [];

  const headers = lines[0].split(',').map(h => h.trim());
  const data = [];

  for (let i = 1; i < lines.length; i++) {
    if (!lines[i].trim()) continue;

    const obj = {};
    const values = parseCSVLine(lines[i]);

    headers.forEach((header, index) => {
      obj[header] = values[index]?.trim() || null;
    });

    data.push(obj);
  }

  return data;
}

/**
 * Parse a single CSV line handling quoted values
 */
function parseCSVLine(line) {
  const result = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    const nextChar = line[i + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      result.push(current);
      current = '';
    } else {
      current += char;
    }
  }

  result.push(current);
  return result;
}

/**
 * Convert string boolean values to actual booleans
 */
export function parseBoolean(value) {
  if (!value || value === '') return null;
  if (typeof value === 'boolean') return value;
  return value.toLowerCase() === 'true' || value.toLowerCase() === 'yes';
}

/**
 * Convert string numbers to actual numbers, handle empty strings
 */
export function parseNumber(value) {
  if (!value || value === '') return null;
  const num = Number(value);
  return isNaN(num) ? null : num;
}

/**
 * Parse ski_areas.csv into database records
 * Filters to only include ski areas with status='operating' and has_downhill=yes
 */
export function parseSkiAreasCSV(csvText) {
  const records = parseCSV(csvText);

  return records
    .filter(row => {
      // Only include operating ski areas with downhill
      const status = row.status?.toLowerCase().trim();
      const hasDownhill = parseBoolean(row.has_downhill);
      return status === 'operating' && hasDownhill === true;
    })
    .map(row => ({
      openskidata_id: row.id || null,
      name: row.name || 'Unknown',
      countries: row.countries || null,
      regions: row.regions || null,
      localities: row.localities || null,
      status: row.status || null,
      has_downhill: parseBoolean(row.has_downhill),
      has_nordic: parseBoolean(row.has_nordic),
      downhill_distance_km: parseNumber(row.downhill_distance_km),
      nordic_distance_km: parseNumber(row.nordic_distance_km),
      vertical_m: parseNumber(row.vertical_m),
      min_elevation_m: parseNumber(row.min_elevation_m),
      max_elevation_m: parseNumber(row.max_elevation_m),
      lift_count: parseNumber(row.lift_count),
      surface_lifts_count: parseNumber(row.surface_lifts_count),
      run_convention: row.run_convention || null,
      wikidata_id: row.wikidata_id || null,
      websites: row.websites || null,
      openskimap: row.openskimap || null,
      lat: parseNumber(row.lat),
      lng: parseNumber(row.lng),
      sources: row.sources || null,
      geometry: row.geometry ? tryParseJSON(row.geometry) : null,
      is_published: false
    }));
}

/**
 * Parse runs.csv into database records
 */
export function parseRunsCSV(csvText) {
  const records = parseCSV(csvText);

  return records.map(row => ({
    openskidata_id: row.id || null,
    name: row.name || 'Unknown',
    ref: row.ref || null,
    ref_fr_cairn: row.ref_fr_cairn || null,
    piste_type: row.piste_type || null,
    difficulty: row.difficulty || null,
    status: row.status || null,
    access: row.access || null,
    countries: row.countries || null,
    regions: row.regions || null,
    localities: row.localities || null,
    ski_area_names: row.ski_area_names || null,
    oneway: parseBoolean(row.oneway),
    duration_sec: parseNumber(row.duration_sec),
    capacity: parseNumber(row.capacity),
    occupancy: row.occupancy || null,
    detachable: parseBoolean(row.detachable),
    bubble: parseBoolean(row.bubble),
    heating: parseBoolean(row.heating),
    inclined_length_m: parseNumber(row.inclined_length_m),
    vertical_m: parseNumber(row.vertical_m),
    speed_m_per_s: parseNumber(row.speed_m_per_s),
    vertical_speed_m_per_s: parseNumber(row.vertical_speed_m_per_s),
    min_elevation_m: parseNumber(row.min_elevation_m),
    max_elevation_m: parseNumber(row.max_elevation_m),
    overall_pitch: parseNumber(row['overall_pitch_%']),
    wikidata_id: row.wikidata_id || null,
    websites: row.websites || null,
    openskimap: row.openskimap || null,
    lat: parseNumber(row.lat),
    lng: parseNumber(row.lng),
    sources: row.sources || null,
    description: row.description || null,
    geometry: row.geometry ? tryParseJSON(row.geometry) : null
  }));
}

/**
 * Parse lifts.csv into database records
 */
export function parseLiftsCSV(csvText) {
  const records = parseCSV(csvText);

  return records.map(row => ({
    openskidata_id: row.id || null,
    name: row.name || 'Unknown',
    ref: row.ref || null,
    ref_fr_cairn: row.ref_fr_cairn || null,
    lift_type: row.lift_type || null,
    status: row.status || null,
    access: row.access || null,
    countries: row.countries || null,
    regions: row.regions || null,
    localities: row.localities || null,
    ski_area_names: row.ski_area_names || null,
    oneway: parseBoolean(row.oneway),
    duration_sec: parseNumber(row.duration_sec),
    capacity: parseNumber(row.capacity),
    occupancy: row.occupancy || null,
    detachable: parseBoolean(row.detachable),
    bubble: parseBoolean(row.bubble),
    heating: parseBoolean(row.heating),
    inclined_length_m: parseNumber(row.inclined_length_m),
    vertical_m: parseNumber(row.vertical_m),
    speed_m_per_s: parseNumber(row.speed_m_per_s),
    vertical_speed_m_per_s: parseNumber(row.vertical_speed_m_per_s),
    min_elevation_m: parseNumber(row.min_elevation_m),
    max_elevation_m: parseNumber(row.max_elevation_m),
    overall_pitch: parseNumber(row['overall_pitch_%']),
    wikidata_id: row.wikidata_id || null,
    websites: row.websites || null,
    openskimap: row.openskimap || null,
    lat: parseNumber(row.lat),
    lng: parseNumber(row.lng),
    sources: row.sources || null,
    description: row.description || null,
    geometry: row.geometry ? tryParseJSON(row.geometry) : null
  }));
}

/**
 * Try to parse JSON, return null if invalid
 */
function tryParseJSON(jsonStr) {
  if (!jsonStr) return null;
  try {
    return JSON.parse(jsonStr);
  } catch {
    return null;
  }
}
